<template>
  <div>
    <!-- Audit Logs List Card -->
    <b-card class="audit-logs-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div class="table-header-left">
          <h5 class="mb-0 audit-log-title">Audit Log</h5>
          <small class="text-muted">View and review system audit logs</small>
        </div>
      </div>

      <!-- Filter and Action Bar -->
      <b-row class="align-items-center mb-2 px-1 gap-2">
        <!-- User Filter -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12">
          <v-select
            v-model="filterObj.userID"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="userOptions"
            :reduce="(val) => val.id"
            label="name"
            placeholder="Filter by User"
            @input="onFilterChange"
            class="filter-control-vselect"
          />
        </b-col>

        <!-- Date Range Filter -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12">
          <flat-pickr
            v-model="filterObj.dateRange"
            :config="dateRangeConfig"
            class="form-control filter-control"
            placeholder="Select Date Range"
            @on-change="onFilterChange"
          />
        </b-col>

        <!-- Search Input -->
        <b-col xl="3" lg="3" md="4" sm="12" cols="12">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search notes..."
            class="filter-control"
            @input="onSearchInput"
            debounce="500"
          />
        </b-col>
        
        <!-- Action Buttons -->
        <b-col class="text-right ml-auto d-flex justify-content-end align-items-center">
          <div class="d-flex gap-2">
            <b-button
              v-if="rights.view"
              variant="outline-secondary"
              class="btn-icon"
              @click="loadAuditLogs"
              v-b-tooltip.hover.top="'Reload'"
              :disabled="dataLoading"
            >
              <feather-icon icon="RotateCwIcon" />
            </b-button>
            <b-button
              v-if="rights.view"
              variant="outline-primary"
              class="btn-icon d-flex align-items-center"
              @click="openPrintReport()"
              :disabled="printing"
              v-b-tooltip.hover.top="'Print'"
            >
              <b-spinner v-if="printing" small variant="primary" class="mr-50" />
              <feather-icon v-else icon="PrinterIcon" class="mr-50" />
              <span>Print</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="option => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>
        
        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalCount }}</strong>
          </span>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar" @scroll="syncScroll">
          <div class="table-scrollbar-content"></div>
        </div>

        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="audit-logs-table"
            :items="auditLogs"
            :fields="tableFields"
            :busy="tableBusy"
            :responsive="false"
            hover
            show-empty
            :empty-text="tableEmptyText"
          >
          <template #head()="data">
            <div>
              <span>{{ $t(data.label) }}</span>
            </div>
          </template>

          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td v-for="(field, index) in tableFields" :key="'skeleton-cell-' + index" class="skeleton-cell">
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(sno)="{ index }">
            <span class="font-weight-medium text-dark">
              {{ (currentPage - 1) * perPage + index + 1 }}
            </span>
          </template>

          <!-- Enhanced User Cell with Avatar -->
          <template #cell(userName)="data">
            <div class="audit-user-cell">
              <div class="audit-user-avatar">
                {{ (data.item.meta_user || data.item.userName || data.item.user || 'U').charAt(0).toUpperCase() }}
              </div>
              <div class="audit-user-info">
                <span class="audit-user-email">{{ data.item.meta_user || data.item.userName || data.item.user || 'N/A' }}</span>
                <b-badge v-if="data.item.userID" variant="light-primary" class="audit-user-id-badge">
                  ID: {{ data.item.userID }}
                </b-badge>
              </div>
            </div>
          </template>

          <template #cell(form)="data">
            <div class="audit-form-cell">
              <feather-icon icon="LayersIcon" size="13" class="audit-form-icon" />
              <span>{{ formatFormName(data.item.form) }}</span>
            </div>
          </template>

          <template #cell(action)="data">
            <b-badge 
              :variant="getActionVariant(data.item.action)"
              pill
              class="audit-action-badge"
            >
              {{ data.item.action || 'N/A' }}
            </b-badge>
          </template>

          <template #cell(entryDate)="data">
            <div class="audit-date-cell">
              <feather-icon icon="ClockIcon" size="12" class="audit-date-icon" />
              <span>{{ formatDate(data.item.entryDate) }}</span>
            </div>
          </template>

          <template #cell(note)="data">
            <div class="note-container">
              <template v-if="isRoleUpdate(data.item.note)">
                <b-badge variant="light-warning" class="role-update-tag">
                  <feather-icon icon="ShieldIcon" size="12" class="mr-25" />
                  {{ getRoleUpdateCompact(data.item.note) }}
                </b-badge>
              </template>
              <div v-else class="text-truncate note-cell" v-b-tooltip.hover :title="data.item.note || 'N/A'">
                {{ data.item.note || '—' }}
              </div>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center justify-content-end">
              <b-button
                v-if="rights.view"
                variant="gradient-primary"
                v-b-tooltip.hover.left
                title="View Details"
                size="sm"
                class="action-btn-table"
                @click.stop="openViewModal(item)"
                :disabled="viewLoading === item.id || deleteLoading === item.id"
              >
                <b-spinner v-if="viewLoading === item.id" small type="grow" />
                <feather-icon v-else icon="EyeIcon" size="16" />
              </b-button>
            </div>
          </template>

          </b-table>
        </div>
      </div>
    </b-card>

    <!-- View Details Modal - Premium Audit Log Style -->
    <b-modal
      v-model="viewModalVisible"
      hide-header
      size="lg"
      centered
      no-close-on-backdrop
      body-class="p-0"
      content-class="audit-log-modal-content"
    >
      <div v-if="selectedLog" class="audit-log-modal-body">
        <!-- Modal Header with gradient accent -->
        <div class="alm-header">
          <div class="alm-header-left">
            <div class="alm-icon-wrap">
              <feather-icon icon="FileTextIcon" size="22" />
            </div>
            <div>
              <h5 class="alm-title mb-0">Audit Log Details</h5>
              <small class="alm-subtitle">System audit trail record</small>
            </div>
          </div>
          <b-button variant="flat-secondary" class="alm-close-btn" @click="viewModalVisible = false">
            <feather-icon icon="XIcon" size="18" />
          </b-button>
        </div>

        <!-- Meta Info Grid (TabulationSheetView-style cards) -->
        <div class="alm-meta-grid">
          <!-- User Card -->
          <div class="alm-meta-card">
            <span class="alm-meta-label">
              <feather-icon icon="UserIcon" size="11" class="mr-25" />USER
            </span>
            <span class="alm-meta-value">{{ selectedLog.meta_user || selectedLog.userName || selectedLog.user || 'N/A' }}</span>
          </div>

          <!-- Form Card -->
          <div class="alm-meta-card">
            <span class="alm-meta-label">
              <feather-icon icon="LayersIcon" size="11" class="mr-25" />FORM / MODULE
            </span>
            <span class="alm-meta-value" v-b-tooltip.hover :title="formatFormName(selectedLog.form)">
              {{ formatFormName(selectedLog.form) }}
            </span>
          </div>

          <!-- Action Card (highlighted) -->
          <div class="alm-meta-card alm-meta-card-accent">
            <span class="alm-meta-label">
              <feather-icon icon="ZapIcon" size="11" class="mr-25" />ACTION
            </span>
            <b-badge :variant="getActionVariant(selectedLog.action)" pill class="alm-action-badge">
              {{ selectedLog.action || 'N/A' }}
            </b-badge>
          </div>

          <!-- Entry Date Card -->
          <div class="alm-meta-card">
            <span class="alm-meta-label">
              <feather-icon icon="CalendarIcon" size="11" class="mr-25" />DATE
            </span>
            <span class="alm-meta-value">{{ formatDate(selectedLog.entryDate) }}</span>
          </div>
        </div>

        <!-- Note Section -->
        <div class="alm-note-section">
          <div class="alm-note-header">
            <feather-icon icon="MessageSquareIcon" size="15" class="alm-note-icon" />
            <span class="alm-note-title">Note / Description</span>
          </div>
          <div class="alm-note-body">
            <template v-if="isRoleUpdate(selectedLog.note)">
              <div class="role-details-container">
                <div class="role-intro-banner">
                  <feather-icon icon="InfoIcon" size="14" class="mr-50" />
                  <span>{{ getRoleIntro(selectedLog.note) }}</span>
                </div>
                
                <div class="rights-changes-wrapper">
                  <h6 class="rights-changes-title">Meaningful Permission Changes</h6>
                  <div class="rights-changes-grid">
                    <div v-for="(change, idx) in parseRightsChanges(selectedLog.note)" :key="idx" class="rights-change-row">
                      <div class="rights-module">
                        <span class="module-text">{{ change.module }}</span>
                      </div>
                      <div class="rights-diff-flow">
                        <div class="rights-state-box old">
                          <span class="state-label">OLD</span>
                          <span class="state-content">{{ change.old }}</span>
                        </div>
                        <div class="rights-connector">
                          <feather-icon icon="ChevronsRightIcon" size="16" />
                        </div>
                        <div class="rights-state-box" :class="getChangeClass(change.old, change.new)">
                          <span class="state-label">NEW</span>
                          <span class="state-content">{{ change.new }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="parseRightsChanges(selectedLog.note).length === 0" class="no-meaningful-changes">
                    No major permission changes detected in this update.
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              {{ selectedLog.note || 'No notes recorded for this log entry.' }}
            </template>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="viewModalVisible = false" class="alm-footer-btn">
          <feather-icon icon="XIcon" size="14" class="mr-50" />
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BBadge,
  BPagination,
  BFormInput,
  BFormGroup,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import axios from "axios";
import Swal from "sweetalert2";
import flatPickr from "vue-flatpickr-component";
import "@core/scss/vue/libs/vue-flatpicker.scss";

export default {
  name: "AuditLogs",
  components: {
    BCard,
    BTable,
    BBadge,
    BPagination,
    BFormInput,
    BFormGroup,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BModal,
    vSelect,
    flatPickr,
  },
  data() {
    return {
      dataLoading: false,
      rights: {},
      viewLoading: null,
      deleteLoading: null,
      auditLogs: [],
      currentPage: 1,
      perPage: 30,
      totalCount: 0,
      totalPages: 1,
      printing: false,
      pageSizeOptions: [
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '50', value: 50 },
        { label: '100', value: 100 },
      ],
      tableFields: [
        { label: "S.NO", key: "sno", sortable: false, thStyle: { width: '70px', textAlign: 'center' }, tdClass: 'text-center' },
        { label: "USER", key: "userName", sortable: false, thStyle: { minWidth: '180px' } },
        { label: "FORM", key: "form", sortable: false, thStyle: { minWidth: '120px' } },
        { label: "ACTION", key: "action", sortable: false, thStyle: { minWidth: '100px' } },
        { label: "ENTRY DATE", key: "entryDate", sortable: false, thStyle: { minWidth: '150px' } },
        { label: "NOTE", key: "note", sortable: false, thStyle: { minWidth: '280px' } },
        { label: "ACTIONS", key: "actions", sortable: false, thStyle: { width: '120px', textAlign: 'right' }, tdClass: 'text-right' },
      ],
      viewModalVisible: false,
      selectedLog: null,
      searchQuery: "",
      userOptions: [],
      filterObj: {
        userID: 0,
        dateRange: null,
      },
      dateRangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
      },
    };
  },
  computed: {
    tableBusy() {
      return this.dataLoading;
    },
    tableEmptyText() {
      return this.tableBusy ? '' : 'No audit logs found';
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  async created() {
    const routeRights = this.$store.state.rights[this.$route.name];
    if (!routeRights) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = routeRights;
    
    await Promise.all([
      this.loadAuditLogs(),
      this.loadUsers()
    ]);
  },
  mounted() {
    this.updateScrollbarWidth();
    window.addEventListener('resize', this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollbarWidth);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async handleDelete(item) {
      const result = await Swal.fire({
        title: 'Delete Log Entry?',
        text: this.$t("You won't be able to revert this!"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ea5455',
        cancelButtonColor: '#82868b',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        this.deleteLoading = item.id;
        try {
          const url = `${this.$store.state.domain}AuditLog/Delete/${item.id}`;
          
          await axios.delete(url, {
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              db: this.$store.state.userData.db
            }
          });

          this.$bvToast.toast('Record deleted successfully', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
          
          this.loadAuditLogs();
        } catch (error) {
          // console.error("Delete error:", error);
          this.$bvToast.toast('Failed to delete record. Please try again.', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        } finally {
          this.deleteLoading = null;
        }
      }
    },

    openPrintReport() {
      this.printing = true;
      try {
        const routeData = this.$router.resolve({
          name: 'audit-log-print',
          query: {
            db: this.$store.state.userData.db,
            cID: this.$store.state.userData.cId,
          }
        });
        window.open(routeData.href, '_blank');
        this.$bvToast.toast('Print report opened in new tab', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        // console.error('Print error:', error);
        this.$bvToast.toast('Failed to open print report. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.printing = false;
      }
    },


    async loadAuditLogs() {
      this.dataLoading = true;
      this.auditLogs = [];

      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
        });

        // Add pagination
        params.append('page', this.currentPage);
        params.append('pageSize', this.perPage);

        // Add Filters
        if (this.filterObj.userID) {
          params.append('userID', this.filterObj.userID);
        }
        if (this.filterObj.dateRange) {
          const dates = this.filterObj.dateRange.split(' to ');
          if (dates.length === 2) {
            params.append('startDate', dates[0]);
            params.append('endDate', dates[1]);
          } else if (dates.length === 1) {
            params.append('startDate', dates[0]);
            params.append('endDate', dates[0]);
          }
        }
        if (this.searchQuery) {
          params.append('search', this.searchQuery);
        }

        const url = `${this.$store.state.domain}AuditLog/GetByPagination?${params.toString()}`;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${this.$store.state.userData.token}` },
        });

        const payload = res?.data ?? res ?? {};
        const container = payload?.data;
        let list = Array.isArray(container?.data) ? container.data : (Array.isArray(container) ? container : (Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : [])));

        const pickNumber = (...vals) => {
          for (const v of vals) {
            const n = Number(v);
            if (v !== undefined && v !== null && !Number.isNaN(n)) return n;
          }
          return null;
        };

        this.auditLogs = list;
        this.totalCount = pickNumber(payload?.totalCount, payload?.totalRecords, container?.totalCount, payload?.total) ?? list.length;
        this.updateScrollbarWidth();
      } catch (error) {
        // console.error("Error loading audit logs:", error);
        this.auditLogs = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load audit logs", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true
        });
      } finally {
        this.dataLoading = false;
      }
    },

    async loadUsers() {
      try {
        const url = `${this.$store.state.domain}Users/LoadUsers?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${this.$store.state.userData.token}` },
        });
        const users = Array.isArray(res.data.data) ? res.data.data : [];
        this.userOptions = [{ name: 'All Users', id: 0 }, ...users.map(u => ({ name: u.name, id: u.id }))];
      } catch (e) {
        // console.error("Error loading users:", e);
      }
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadAuditLogs();
    },

    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadAuditLogs();
      }, 500);
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadAuditLogs();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadAuditLogs();
    },

    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector('.vs__dropdown-toggle');
          if (toggle) toggle.blur();
        }
      });
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateString;
      }
    },

    formatFormName(form) {
      if (!form) return 'N/A';
      return form.replace(/\b\w/g, l => l.toUpperCase());
    },

    openViewModal(item) {
      this.selectedLog = { ...item };
      this.viewModalVisible = true;
    },

    getActionVariant(action) {
      if (!action) return 'light-secondary';
      const act = action.toLowerCase();
      if (act === 'delete' || act.includes('delete')) return 'light-danger';
      if (act === 'create' || act === 'add' || act.includes('create') || act.includes('add')) return 'light-success';
      if (act === 'update' || act === 'edit' || act.includes('update') || act.includes('edit')) return 'light-warning';
      if (act === 'view' || act === 'read' || act.includes('view') || act.includes('read')) return 'light-info';
      return 'light-secondary';
    },


    syncScroll(event) {
      const tableWrapper = this.$refs.tableWrapper;
      const topScrollbar = this.$refs.topScrollbar;
      if (!tableWrapper || !topScrollbar) return;
      if (event.target === tableWrapper) topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      else if (event.target === topScrollbar) tableWrapper.scrollLeft = topScrollbar.scrollLeft;
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const scrollContent = topScrollbar?.querySelector('.table-scrollbar-content');
        if (tableWrapper && scrollContent) {
          scrollContent.style.width = `${tableWrapper.scrollWidth}px`;
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      });
    },

    getStatusClass(status) {
      if (!status) return "status-neutral";
      const s = status.toLowerCase();
      return (s.includes('success') || s.includes('active') || s.includes('completed')) ? "status-passed" : ((s.includes('fail') || s.includes('error') || s.includes('inactive')) ? "status-failed" : "status-neutral");
    },

    // Role Note Helper Methods
    isRoleUpdate(note) {
      if (!note) return false;
      return note.includes("Updated role:") && note.includes("Rights updated:");
    },

    getRoleUpdateCompact(note) {
      if (!note) return "";
      const match = note.match(/Updated role:\s*(.*?)\s*-/);
      return `Role: ${match ? match[1] : 'Updated'}`;
    },

    getRoleIntro(note) {
      if (!note) return "";
      const parts = note.split("Rights updated:");
      return parts[0].replace("-", "").trim();
    },

    parseRightsChanges(note) {
      if (!note) return [];
      const parts = note.split("Rights updated:");
      if (parts.length < 2) return [];
      
      const rightsStr = parts[1].trim();
      const segments = rightsStr.split(", ").filter(s => s.includes("→"));

      const isNone = (val) => !val || val === "[]" || val === "None" || val === "{}";

      return segments.map(seg => {
        // Regex to match "ModuleName: [Old] → [New]"
        const match = seg.match(/(.*?):\s*\[(.*?)\]\s*→\s*\[(.*?)\]/);
        if (match) {
          const oldVal = match[2].trim() || "None";
          const newVal = match[3].trim() || "None";
          
          // Skip if no real change (e.g., [] to None)
          if (isNone(oldVal) && isNone(newVal)) return null;
          if (oldVal === newVal) return null;

          return {
            module: match[1].trim(),
            old: oldVal,
            new: newVal
          };
        }
        return null;
      }).filter(item => item !== null);
    },

    getChangeClass(oldVal, newVal) {
      const isNone = (val) => !val || val === "[]" || val === "None" || val === "{}";
      if (isNone(newVal)) return "new-revoked";
      if (isNone(oldVal)) return "new-granted";
      return "new-updated";
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

// Audit Logs Card - Matching marksRecord.vue clean UI style
.audit-logs-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }
}

.audit-log-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-y-2 {
  row-gap: 0.5rem;
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

// Pagination Controls - Top (Compact Single Line)
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;
    
    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
  
  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }
  
  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

.filters-bar {
  padding: 0 0 0.25rem 0;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.filter-item {
  min-width: 170px;
}

.print-button-filter {
  min-width: auto;
  flex-shrink: 0;
  margin-left: auto;
  
  @media (max-width: 992px) {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  }
}

.filters-bar .vs__dropdown-toggle,
.filters-bar .form-control {
  border-radius: 14px;
}

.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 12px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus,
  &:focus-within {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

.filter-control.v-select {
  width: 100%;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;

  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 12px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.5rem 0.75rem !important;
    background: #fff !important;
    transition: all 0.2s ease !important;

    &:hover {
      border-color: #b8a9ff !important;
    }
  }

  ::v-deep .vs__search::placeholder,
  ::v-deep .vs__dropdown-toggle {
    color: #2d1b69;
    font-weight: 500;
  }

  ::v-deep .vs__selected {
    color: #2d1b69;
    font-weight: 500;
  }

  ::v-deep .vs__actions {
    padding: 0 0.5rem 0 0.25rem;
  }

  ::v-deep .vs__clear {
    fill: #9ca3af;
  }

  ::v-deep .vs__open-indicator {
    fill: #7c5dfa;
  }
}

// Table Wrapper for Horizontal Scroll
.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.table-wrapper {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.audit-logs-table {
  min-width: 1200px;
  border: 1px solid #e5e7eb;
  border-collapse: separate;
  border-spacing: 0;
  
  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.875rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;

    &::after {
      display: none;
    }
  }
  
  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.85rem 1rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
  }
  
  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 3px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;
      td { background: #f8fafc; }
    }

    &:hover {
      background: #f3f0ff !important;
      border-left-color: #7c5dfa;
      td {
        background: #f3f0ff !important;
        color: #2d1b69;
      }
    }
  }
}

// User cell with avatar chip
.audit-user-cell {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.audit-user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.85rem;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(124, 93, 250, 0.25);
  letter-spacing: -0.5px;
}

.audit-user-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.audit-user-email {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.875rem;
  line-height: 1.2;
}

.audit-user-id-badge {
  font-size: 0.7rem !important;
  padding: 0.15em 0.5em !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  align-self: flex-start;
}

// Form cell
.audit-form-cell {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 600;
  color: #374151;
}

.audit-form-icon {
  color: #7c5dfa;
  flex-shrink: 0;
  opacity: 0.8;
}

// Action badge
.audit-action-badge {
  font-size: 0.77rem !important;
  padding: 0.3em 0.75em !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
}

// Date cell
.audit-date-cell {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #475569;
  font-weight: 500;
}

.audit-date-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

.page-size-select-compact {
  min-width: 85px;
  width: 85px;
  
  ::v-deep .vs__dropdown-toggle {
    height: 38px !important;
    min-height: 38px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e2e8f0 !important;
    padding: 0 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    display: flex;
    align-items: center;
    
    &:hover {
      border-color: #7367f0 !important;
    }
  }
  
  ::v-deep .vs__selected {
    color: #334155 !important;
    font-weight: 600 !important;
    font-size: 0.875rem !important;
    margin: 0 !important;
    padding: 0 !important;
    position: relative !important;
    top: 1px;
  }

  ::v-deep .vs__actions {
    padding: 0 !important;
    margin-top: -2px;
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  
  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    
    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

// Square-ish Pagination Style
.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;
    gap: 6px;
    
    .page-item {
      .page-link {
        padding: 0;
        font-size: 0.85rem;
        line-height: 1;
        border-radius: 10px !important;
        margin: 0;
        border: 1.5px solid #e9e5ff;
        color: #64748b;
        font-weight: 600;
        min-width: 38px;
        height: 38px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fbfaff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        
        &:hover {
          background: #f0edff;
          border-color: #7c5dfa;
          color: #7c5dfa;
          transform: translateY(-2px);
          box-shadow: 0 4px 10px rgba(115, 103, 240, 0.1);
        }

        &:focus {
          box-shadow: none;
        }
      }

      &.active .page-link {
        background: #7c5dfa !important;
        border-color: #7c5dfa !important;
        color: #fff !important;
        font-weight: 700;
        box-shadow: 0 4px 14px rgba(115, 103, 240, 0.4) !important;
        z-index: 1;
      }

      &.disabled .page-link {
        background: #f8fafc;
        border-color: #efebff;
        color: #b9b9c3;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }

      &.prev-item, &.next-item, &.first-item, &.last-item {
        .page-link {
          font-size: 1.1rem;
          color: #b9b9c3;
          background: #f8f7ff;
        }
      }
    }
  }
}

.status-passed { background: rgba(16, 185, 129, 0.1); color: #059669; }
.status-failed { background: rgba(239, 68, 68, 0.1); color: #dc2626; }
.status-neutral { background: rgba(107, 114, 128, 0.1); color: #4b5563; }

.note-cell {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Action Button - Same as resource.vue
.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Premium Audit Log Modal Styles
.audit-log-modal-content {
  border-radius: 20px !important;
  overflow: hidden;
  border: 1.5px solid #e0d8ff !important;
  box-shadow: 0 20px 60px rgba(115, 103, 240, 0.2) !important;
}

.audit-log-modal-body {
  padding: 0;
}

// Modal Header
.alm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20px;
    width: 180px;
    height: 180px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 50%;
  }
}

.alm-header-left {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  position: relative;
  z-index: 1;
}

.alm-icon-wrap {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
}

.alm-title {
  font-size: 1.1rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.3px;
}

.alm-subtitle {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
}

.alm-close-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white !important;
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  position: relative;
  z-index: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.25) !important;
  }
}

// Meta Cards Grid (inspired by TabulationSheetView)
.alm-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border-bottom: 1.5px solid #f0edff;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.alm-meta-card {
  background: white;
  border: 1.5px solid #f0edff;
  border-radius: 14px;
  padding: 1rem 1.1rem;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
    border-radius: 0 0 0 14px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.14);
    border-color: #d4c7ff;

    &::before {
      transform: scaleY(1);
    }
  }

  &.alm-meta-card-accent {
    border-color: #e0d8ff;
    background: linear-gradient(135deg, #faf8ff 0%, #f3f0ff 100%);
  }
}

.alm-meta-label {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.alm-meta-value {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #2d1b69;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.alm-action-badge {
  font-size: 0.8rem !important;
  padding: 0.35em 0.75em !important;
  font-weight: 700 !important;
}

// Note Section
.alm-note-section {
  padding: 1.25rem 1.5rem 0.5rem;
}

.alm-note-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.625rem;
  border-bottom: 2px solid #f0edff;
}

.alm-note-icon {
  color: #7c5dfa;
}

.alm-note-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.alm-note-body {
  background: #f8f9fc;
  border: 1.5px solid #f0edff;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  color: #1e293b;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.7;
  white-space: pre-wrap;
  word-wrap: break-word;
  min-height: 100px;
  max-height: 280px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 6px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
}

.alm-footer-btn {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border: 1.5px solid #e0d8ff;
  color: #64748b;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #b8a9ff;
    color: #7c5dfa;
  }
}

// Role Update Styles
.role-update-tag {
  font-size: 0.8rem;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}

.role-details-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.role-intro-banner {
  background: white;
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-weight: 700;
  color: #2d1b69;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 10px rgba(115, 103, 240, 0.05);
}

.rights-changes-wrapper {
  background: white;
  border: 1px solid #f0edff;
  border-radius: 14px;
  padding: 1rem;
}

.rights-changes-title {
  font-size: 0.75rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
  display: block;
}

.rights-changes-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rights-change-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1.1rem;
  background: #f8fbff;
  border: 1px solid #edf2f9;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: #7c5dfa;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.rights-module {
  min-width: 160px;
  .module-text {
    font-weight: 800;
    color: #475569;
    font-size: 0.9rem;
  }
}

.rights-diff-flow {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-grow: 1;
}

.rights-state-box {
  flex: 1;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  flex-direction: column;
  min-width: 80px;

  .state-label {
    font-size: 0.6rem;
    font-weight: 800;
    color: #94a3b8;
    margin-bottom: 2px;
  }

  .state-content {
    font-weight: 700;
    font-size: 0.85rem;
    color: #1e293b;
  }

  &.old { border-left: 3px solid #cbd5e1; }
  
  &.new-granted {
    background: rgba(16, 185, 129, 0.08);
    border-left: 3px solid #10b981;
    .state-content { color: #059669; }
  }
  
  &.new-revoked {
    background: rgba(239, 68, 68, 0.08);
    border-left: 3px solid #ef4444;
    .state-content { color: #dc2626; }
  }

  &.new-updated {
    background: rgba(245, 158, 11, 0.08);
    border-left: 3px solid #f59e0b;
    .state-content { color: #d97706; }
  }
}

.rights-connector {
  color: #94a3b8;
  display: flex;
  align-items: center;
}

.no-meaningful-changes {
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
  font-style: italic;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px dashed #e2e8f0;
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
